import React, { useMemo } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import DatePicker from "../../Common/DatePicker";
import {
  formatPayMonthLabel,
  getNextPayMonth,
  getPreviousPayMonth,
} from "../payroll/runPayrollUtils";
import { clampYm } from "../../../utils/selectableDateBounds";

function toMonthDay(ym, day = "01") {
  const s = String(ym || "").slice(0, 7);
  if (!/^\d{4}-\d{2}$/.test(s)) return "";
  return `${s}-${day}`;
}

/**
 * Pay / attendance month control: prev · custom month DatePicker · next.
 * Value stays YYYY-MM; selecting a month loads that month via onChange (unchanged).
 */
export default function PayMonthNav({
  value = "",
  onChange,
  min = "",
  max = "",
  disabled = false,
  darkMode = false,
  label = "Pay month",
  id = "pay-month-nav",
}) {
  const ym = String(value || "").slice(0, 7);
  const display = useMemo(() => formatPayMonthLabel(ym), [ym]);
  const pickerValue = useMemo(() => toMonthDay(ym, "01"), [ym]);
  const pickerMin = useMemo(
    () => (min ? toMonthDay(min, "01") : undefined),
    [min]
  );
  const pickerMax = useMemo(() => {
    if (!max) return undefined;
    // Last day of max month so the whole month stays selectable.
    const s = String(max).slice(0, 7);
    const m = s.match(/^(\d{4})-(\d{2})$/);
    if (!m) return toMonthDay(max, "01");
    const last = new Date(Number(m[1]), Number(m[2]), 0).getDate();
    return `${s}-${String(last).padStart(2, "0")}`;
  }, [max]);

  const prev = getPreviousPayMonth(ym);
  const next = getNextPayMonth(ym);
  const prevOk =
    !!prev &&
    (!min || prev >= String(min).slice(0, 7)) &&
    clampYm(prev, min || null, max || null) === prev;
  const nextOk =
    !!next &&
    (!max || next <= String(max).slice(0, 7)) &&
    clampYm(next, min || null, max || null) === next;

  const go = (target) => {
    if (!target || disabled) return;
    const clamped = clampYm(target, min || null, max || null);
    if (clamped && clamped !== ym) onChange?.(clamped);
  };

  const shell = darkMode
    ? "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-muted))] text-white"
    : "border-[hsl(var(--ds-border))] bg-[hsl(var(--ds-surface))] text-[hsl(var(--ds-fg))]";

  return (
    <div>
      <div id={`${id}-label`} className="mb-1 block text-xs font-medium">
        {label}
      </div>
      <div
        className="flex items-center gap-1"
        role="group"
        aria-labelledby={`${id}-label`}
      >
        <button
          type="button"
          disabled={disabled || !prevOk}
          onClick={() => go(prev)}
          title={prevOk ? formatPayMonthLabel(prev) : undefined}
          aria-label="Previous month"
          className={`inline-flex h-9 w-8 items-center justify-center rounded border disabled:opacity-40 ${shell}`}
        >
          <ChevronLeft size={16} />
        </button>
        <div className="min-w-[10.5rem]" title={display || ym}>
          <DatePicker
            compact
            portal
            darkMode={darkMode}
            monthYearOnly
            showMonthName
            value={pickerValue}
            min={pickerMin}
            max={pickerMax}
            disabled={disabled}
            dropdownAlign="right"
            onChange={(e) => go(String(e?.target?.value || "").slice(0, 7))}
            className="!mb-0"
          />
        </div>
        <button
          type="button"
          disabled={disabled || !nextOk}
          onClick={() => go(next)}
          title={nextOk ? formatPayMonthLabel(next) : undefined}
          aria-label="Next month"
          className={`inline-flex h-9 w-8 items-center justify-center rounded border disabled:opacity-40 ${shell}`}
        >
          <ChevronRight size={16} />
        </button>
      </div>
    </div>
  );
}
